import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_URL, fetchWithAuth } from '../api';
import { 
  UserPlus, Users, X, Check, Activity, Plus, UserMinus, 
  Search, MessageSquare, Trash2, Shield
} from 'react-feather';
import { FaCrown } from 'react-icons/fa';
import Footer from '../components/Footer';

// --- SKELETONS (COMPONENTES DE CARGA) ---

const GroupSkeleton = () => (
  <div className="bg-[#121217] border border-white/5 rounded-3xl p-6 h-[250px] animate-pulse flex flex-col justify-between">
    <div>
      <div className="flex justify-between mb-4">
        <div className="h-6 bg-white/10 rounded w-1/2" />
        <div className="h-6 w-6 bg-white/5 rounded" />
      </div>
      <div className="h-4 bg-white/5 rounded w-full mb-2" />
      <div className="h-4 bg-white/5 rounded w-2/3" />
    </div>
    <div>
      <div className="h-3 w-16 bg-white/5 rounded mb-3" />
      <div className="flex gap-2">
        <div className="w-8 h-8 rounded-full bg-white/10" />
        <div className="w-8 h-8 rounded-full bg-white/10" />
        <div className="w-8 h-8 rounded-full bg-white/10" />
      </div>
    </div>
  </div>
);

const FriendSkeleton = () => (
  <div className="bg-[#121217] border border-white/5 rounded-2xl p-4 flex items-center gap-4 animate-pulse">
    <div className="w-14 h-14 rounded-full bg-white/10 shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-4 bg-white/10 rounded w-3/4" />
      <div className="h-3 bg-white/5 rounded w-1/2" />
    </div>
  </div>
);

const RequestSkeleton = () => (
  <div className="bg-[#121217] border border-white/5 rounded-2xl p-5 flex items-center justify-between animate-pulse">
    <div className="flex items-center gap-4 w-full">
      <div className="w-12 h-12 rounded-full bg-white/10 shrink-0" />
      <div className="space-y-2 w-full">
        <div className="h-4 bg-white/10 rounded w-1/3" />
        <div className="h-3 bg-white/5 rounded w-1/2" />
      </div>
    </div>
    <div className="flex gap-2">
      <div className="w-8 h-8 rounded bg-white/10" />
      <div className="w-8 h-8 rounded bg-white/10" />
    </div>
  </div>
);

export default function Friends() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // --- DATA STATES ---
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [groups, setGroups] = useState([]);
  
  // --- UI STATES ---
  const [activeTab, setActiveTab] = useState('groups');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // --- MODAL/FORM STATES ---
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '', member_usernames: [] });
  
  // Search / Add Friend States
  const [newFriendUsername, setNewFriendUsername] = useState('');
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  // Group Management States
  const [addMemberGroupId, setAddMemberGroupId] = useState(null);
  const [addMemberUsername, setAddMemberUsername] = useState('');
  const [addMemberSuggestions, setAddMemberSuggestions] = useState([]);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const addMemberInputRef = useRef(null);

  // Delete Confirmation States
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // --- INITIAL LOAD ---
  useEffect(() => {
    const loadSocialData = async () => {
      try {
        setLoading(true);
        // await new Promise(r => setTimeout(r, 1000)); // Descomentar para probar skeletons
        await Promise.all([fetchFriends(), fetchRequests(), fetchGroups()]);
      } catch (err) {
        console.error('Failed to fetch social data:', err);
      } finally {
        setLoading(false);
      }
    };
    if(user) loadSocialData();
  }, [user]);

  // --- API CALLS (Mismas funciones que antes) ---
  const fetchGroups = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/groups`);
      setGroups(response);
    } catch (err) { setError('Error cargando grupos'); }
  };

  const fetchFriends = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/friends`);
      setFriends(response);
    } catch (err) { setError('Error cargando amigos'); }
  };

  const fetchRequests = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/friends/requests/received`);
      setRequests(response);
    } catch (err) { setError('Error cargando solicitudes'); }
  };

  const fetchUsernameSuggestions = async (query, setSuggestions) => {
    if (!query) { setSuggestions([]); return; }
    try {
      const res = await fetch(`${API_URL}/public/search-usernames?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data);
      } else { setSuggestions([]); }
    } catch { setSuggestions([]); }
  };

  // --- ACTIONS ---
  const sendFriendRequest = async (e) => {
    e.preventDefault();
    try {
      await fetchWithAuth(`${API_URL}/friends/request`, {
        method: 'POST',
        body: JSON.stringify({ username: newFriendUsername })
      });
      showSuccess('Solicitud enviada correctamente');
      setNewFriendUsername('');
    } catch (err) { showError(err.message); }
  };

  const handleRequest = async (senderId, action) => {
    try {
      await fetchWithAuth(`${API_URL}/friends/${action}/${senderId}`, { method: 'POST' });
      setRequests(prev => prev.filter(req => req.sender_id !== senderId));
      if (action === 'accept') fetchFriends();
    } catch (err) { showError(`Error al ${action === 'accept' ? 'aceptar' : 'rechazar'} solicitud`); }
  };

  const createGroup = async (e) => {
    e.preventDefault();
    try {
      await fetchWithAuth(`${API_URL}/groups`, { method: 'POST', body: JSON.stringify(newGroup) });
      showSuccess('Grupo creado correctamente');
      setShowCreateGroup(false);
      setNewGroup({ name: '', description: '', member_usernames: [] });
      fetchGroups();
    } catch (err) { showError(err.message); }
  };

  const handleGlobalDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'friend') {
        await fetchWithAuth(`${API_URL}/friends/${deleteTarget.id}`, { method: 'DELETE' });
        setFriends(prev => prev.filter(f => f.friend.id !== deleteTarget.id));
      } else if (deleteTarget.type === 'group') {
        await fetchWithAuth(`${API_URL}/groups/${deleteTarget.id}`, { method: 'DELETE' });
        setGroups(prev => prev.filter(g => g.id !== deleteTarget.id));
      }
      setDeleteTarget(null);
    } catch (err) {
      setError(err?.message || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const addMemberToGroup = async (e) => {
    e.preventDefault();
    if (!addMemberGroupId || !addMemberUsername) return;
    setAddMemberLoading(true);
    try {
      await fetchWithAuth(`${API_URL}/groups/${addMemberGroupId}/members/${addMemberUsername}`, { method: 'POST' });
      await fetchGroups();
      showSuccess('Miembro añadido');
      setAddMemberUsername('');
      setAddMemberSuggestions([]);
      setAddMemberGroupId(null);
    } catch (err) { showError('Error añadiendo miembro'); } 
    finally { setAddMemberLoading(false); }
  };

  const removeMemberFromGroup = async (groupId, username) => {
    if (!window.confirm(`¿Eliminar a ${username} del grupo?`)) return;
    try {
      await fetchWithAuth(`${API_URL}/groups/${groupId}/members/${username}`, { method: 'DELETE' });
      setGroups(groups.map(g => {
        if (g.id === groupId) {
          return { ...g, members: g.members.filter(m => m.username !== username) };
        }
        return g;
      }));
    } catch (err) { showError('Error eliminando miembro'); }
  };

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };
  const showError = (msg) => { setError(msg); setTimeout(() => setError(''), 3000); };
  
  const toggleNewGroupMember = (username) => {
    setNewGroup(prev => {
      const exists = prev.member_usernames.includes(username);
      return {
        ...prev,
        member_usernames: exists 
          ? prev.member_usernames.filter(u => u !== username) 
          : [...prev.member_usernames, username]
      };
    });
  };

  if (!user) return null;

  // NOTA: ELIMINAMOS EL "IF LOADING RETURN" BLOQUEANTE

  return (
    <div className="min-h-screen bg-[#020205] text-white font-sans selection:bg-purple-500/30">
      
      {/* Background Ambience */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 pt-24 sm:pt-32">
        
        {/* HEADER & TABS (Siempre visible, incluso cargando) */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 animate-fade-in">
          <div>
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
              <span className="bg-clip-text text-transparent text-white">
                Social Hub
              </span>
            </h1>
            <p className="text-gray-400 max-w-lg">Gestiona tus grupos, conecta con amigos y planifica tus salidas.</p>
          </div>
          
          {/* Custom Tab Navigation */}
          <div className="flex p-1 bg-white/5 backdrop-blur-md rounded-xl border border-white/10">
            {[
              { id: 'groups', label: 'Mis Grupos', icon: Shield },
              { id: 'friends', label: 'Amigos', icon: Users },
              { id: 'requests', label: 'Solicitudes', icon: UserPlus, badge: loading ? 0 : requests.length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  relative flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-300
                  ${activeTab === tab.id 
                    ? 'bg-white text-black shadow-lg shadow-white/10' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'}
                `}
              >
                <tab.icon size={16} />
                {tab.label}
                {tab.badge > 0 && !loading && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white animate-pulse">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ALERTS */}
        {error && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-center animate-fade-in">{error}</div>}
        {success && <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400 text-center animate-fade-in">{success}</div>}

        {/* --- CONTENT AREA --- */}
        <div className="min-h-[70vh]">
          
          {/* TAB 1: GROUPS */}
          {activeTab === 'groups' && (
            <div className="space-y-6">
              
              {/* Botón de crear (Solo visible si ya cargó para evitar saltos, o visible siempre si prefieres) */}
              {!loading && !showCreateGroup && (
                <button
                  onClick={() => setShowCreateGroup(true)}
                  className="w-full py-4 border border-dashed border-white/20 rounded-2xl text-gray-400 hover:text-white hover:border-purple-500/50 hover:bg-purple-500/5 transition-all flex items-center justify-center gap-2 group animate-fade-in"
                >
                  <div className="p-2 bg-white/5 rounded-full group-hover:bg-purple-500 group-hover:text-white transition-colors">
                    <Plus size={20} />
                  </div>
                  <span className="font-medium">Crear Nuevo Grupo</span>
                </button>
              )}

              {/* Create Group Form */}
              {showCreateGroup && (
                <div className="bg-[#121217] border border-white/10 rounded-2xl p-6 md:p-8 relative overflow-hidden animate-fade-in">
                  <div className="absolute top-0 right-0 p-4">
                     <button onClick={() => setShowCreateGroup(false)} className="text-gray-500 hover:text-white"><X/></button>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-6">Nuevo Grupo</h3>
                  <form onSubmit={createGroup} className="space-y-5">
                    {/* ... (Formulario igual que antes) ... */}
                    <div className="grid md:grid-cols-2 gap-5">
                       <div className="space-y-4">
                          <input
                            placeholder="Nombre del Grupo"
                            value={newGroup.name}
                            onChange={e => setNewGroup({...newGroup, name: e.target.value})}
                            className="w-full bg-black border border-white/10 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none transition-colors"
                          />
                          <textarea
                            placeholder="Descripción (opcional)"
                            value={newGroup.description}
                            onChange={e => setNewGroup({...newGroup, description: e.target.value})}
                            className="w-full bg-black border border-white/10 rounded-xl px-4 py-3 text-white focus:border-purple-500 focus:outline-none transition-colors h-32 resize-none"
                          />
                       </div>
                       
                       <div className="bg-black/50 border border-white/10 rounded-xl p-4">
                          <p className="text-sm text-gray-400 mb-3">Seleccionar Integrantes:</p>
                          <div className="h-48 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                            {friends.length === 0 ? <p className="text-xs text-gray-600">No tienes amigos para agregar.</p> :
                             friends.map(({friend}) => (
                               <div 
                                 key={friend.id}
                                 onClick={() => toggleNewGroupMember(friend.username)}
                                 className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${newGroup.member_usernames.includes(friend.username) ? 'bg-purple-600/20 border border-purple-500/40' : 'bg-white/5 hover:bg-white/10 border border-transparent'}`}
                               >
                                 <span className="text-sm font-medium">{friend.name}</span>
                                 {newGroup.member_usernames.includes(friend.username) && <Check size={16} className="text-purple-400"/>}
                               </div>
                             ))
                            }
                          </div>
                       </div>
                    </div>
                    <div className="flex justify-end pt-2">
                       <button type="submit" className="bg-white text-black px-6 py-2 rounded-lg font-bold hover:bg-gray-200 transition-colors">
                         Confirmar Creación
                       </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Group List Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                 {loading ? (
                    // Muestra 3 esqueletos mientras carga
                    <>
                      <GroupSkeleton />
                      <GroupSkeleton />
                      <GroupSkeleton />
                    </>
                 ) : (
                   groups.map(group => {
                    const isCreator = group.creator_id === user.id;
                    return (
                      <div key={group.id} className="group relative bg-[#121217] border border-white/5 rounded-3xl p-6 flex flex-col justify-between min-h-[260px] hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-900/5 hover:-translate-y-1">
                        
                        {/* SECCIÓN SUPERIOR: Info del Grupo */}
                        <div>
                          <div className="flex justify-between items-start mb-3">
                              <h3 className="text-xl font-bold text-white tracking-tight group-hover:text-purple-400 transition-colors">
                                {group.name}
                              </h3>
                              {isCreator && (
                                <button 
                                  onClick={() => setDeleteTarget({ type: 'group', id: group.id, name: group.name })}
                                  className="text-white/20 hover:text-red-400 p-1 transition-colors"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                          </div>
                          <p className="text-sm text-gray-500 font-medium leading-relaxed line-clamp-3">
                            {group.description || "Sin descripción disponible."}
                          </p>
                        </div>

                        {/* SECCIÓN INFERIOR: Miembros y Acciones (Pinned to bottom) */}
                        <div className="pt-6 mt-4 border-t border-white/5">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">Miembros</span>
                              
                              {isCreator && (
                                <button 
                                  onClick={() => setAddMemberGroupId(addMemberGroupId === group.id ? null : group.id)}
                                  className={`
                                    flex items-center gap-1 text-[10px] font-bold px-3 py-1.5 rounded-full border transition-all
                                    ${addMemberGroupId === group.id 
                                      ? 'bg-purple-500 text-white border-purple-500' 
                                      : 'bg-white/5 text-purple-400 border-white/5 hover:bg-purple-500/10 hover:border-purple-500/30'}
                                  `}
                                >
                                    {addMemberGroupId === group.id ? <X size={10}/> : <Plus size={10}/>}
                                    {addMemberGroupId === group.id ? 'CANCELAR' : 'AGREGAR'}
                                </button>
                              )}
                            </div>
                            
                            {/* Input para agregar (Animado) */}
                            {addMemberGroupId === group.id && (
                              <form onSubmit={addMemberToGroup} className="mb-4 relative animate-fade-in-down">
                                <div className="flex gap-2">
                                  <div className="relative flex-1">
                                    <input 
                                      ref={addMemberInputRef}
                                      value={addMemberUsername}
                                      onChange={(e) => {
                                        setAddMemberUsername(e.target.value);
                                        fetchUsernameSuggestions(e.target.value, setAddMemberSuggestions);
                                      }}
                                      placeholder="Username..."
                                      className="w-full bg-[#0a0a0c] border border-purple-500/30 rounded-lg px-3 py-2 text-xs text-white focus:border-purple-500 outline-none placeholder-gray-600"
                                      autoFocus
                                    />
                                    {/* Sugerencias */}
                                    {addMemberSuggestions.length > 0 && (
                                        <div className="absolute bottom-full mb-1 w-full bg-[#1A1A20] border border-white/10 rounded-lg shadow-xl z-50 max-h-32 overflow-y-auto">
                                          {addMemberSuggestions.map(u => (
                                            <div key={u} onClick={() => { setAddMemberUsername(u); setAddMemberSuggestions([]); }} className="px-3 py-2 hover:bg-white/10 cursor-pointer text-xs text-gray-300">@{u}</div>
                                          ))}
                                        </div>
                                    )}
                                  </div>
                                  <button type="submit" disabled={addMemberLoading} className="bg-purple-600 text-white px-3 rounded-lg hover:bg-purple-500 transition-colors">
                                    <Check size={14}/>
                                  </button>
                                </div>
                              </form>
                            )}

                            {/* Lista de Miembros (Chips más limpios) */}
                            <div className="flex flex-wrap items-center gap-2">
                              {group.members.slice(0, 6).map((member, index) => { // Limitamos visualmente a 6 para no romper el diseño
                                  const isGroupCreator = member.id === group.creator_id || index === 0; 
                                  
                                  return (
                                    <div key={member.id} className="relative group/member cursor-default">
                                      <div className={`
                                        flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-[11px] font-medium transition-colors
                                        ${isGroupCreator 
                                          ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-200' 
                                          : 'bg-white/5 border-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200'}
                                      `}>
                                          {isGroupCreator && <FaCrown size={10} className="text-yellow-400" />}
                                          <span className="truncate max-w-[80px]">{member.username}</span>
                                          
                                          {isCreator && member.id !== user.id && (
                                            <button 
                                              onClick={(e) => { e.stopPropagation(); removeMemberFromGroup(group.id, member.username); }}
                                              className="text-gray-600 hover:text-red-400 ml-1"
                                            >
                                              <X size={10}/>
                                            </button>
                                          )}
                                      </div>
                                    </div>
                                  );
                              })}
                              {group.members.length > 6 && (
                                  <span className="text-[10px] text-gray-600 font-bold">+{group.members.length - 6}</span>
                              )}
                            </div>
                        </div>
                      </div>
                    );
                   })
                 )}
                 {/* Empty State */}
                 {!loading && groups.length === 0 && !showCreateGroup && (
                    <div className="col-span-full py-20 text-center text-gray-500 border border-dashed border-white/10 rounded-3xl animate-fade-in">
                       <p>No perteneces a ningún grupo aún.</p>
                    </div>
                 )}
              </div>
            </div>
          )}

          {/* TAB 2: FRIENDS */}
          {activeTab === 'friends' && (
             <div className="space-y-8">
                {/* Search Bar (Siempre visible) */}
                <div className="bg-[#121217] border border-white/10 rounded-2xl p-1 relative z-30">
                   <form onSubmit={sendFriendRequest} className="flex items-center">
                      <div className="pl-4 text-gray-400"><Search size={20}/></div>
                      <div className="flex-1 relative">
                         <input 
                            ref={inputRef}
                            value={newFriendUsername}
                            onChange={e => {
                               setNewFriendUsername(e.target.value);
                               setShowSuggestions(true);
                               fetchUsernameSuggestions(e.target.value, setUsernameSuggestions);
                            }}
                            placeholder="Buscar usuarios por username para agregar..."
                            className="w-full bg-transparent border-none text-white px-4 py-4 focus:ring-0 placeholder-gray-500"
                         />
                         {showSuggestions && usernameSuggestions.length > 0 && (
                            <div className="absolute top-full left-0 w-full mt-2 bg-[#1A1A20] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                               {usernameSuggestions.map(u => (
                                  <div 
                                    key={u} 
                                    className="px-6 py-3 hover:bg-white/5 cursor-pointer border-b border-white/5 last:border-0 flex items-center justify-between group"
                                    onClick={() => { setNewFriendUsername(u); setUsernameSuggestions([]); setShowSuggestions(false); }}
                                  >
                                    <span>@{u}</span>
                                    <span className="text-xs text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">Seleccionar</span>
                                  </div>
                               ))}
                            </div>
                         )}
                      </div>
                      <button type="submit" disabled={!newFriendUsername} className="bg-white text-black px-6 py-2 rounded-xl font-bold mr-2 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                         Enviar Solicitud
                      </button>
                   </form>
                </div>

                {/* Friends Grid */}
                <div>
                   <h3 className="text-lg font-medium text-white mb-4 pl-1">
                      {loading ? 'Cargando amigos...' : `Mis Amigos (${friends.length})`}
                   </h3>
                   
                   <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {loading ? (
                          // Skeletons de amigos
                          Array(8).fill(0).map((_, i) => <FriendSkeleton key={i} />)
                      ) : (
                          // Lista real
                          friends.map(({friend, events_in_common}) => (
                             <div key={friend.id} onClick={() => navigate(`/profile/${friend.id}`)} className="bg-[#121217] border border-white/10 rounded-2xl p-4 hover:border-white/20 transition-all cursor-pointer group flex items-center gap-4 animate-fade-in">
                                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-xl font-bold text-white shrink-0">
                                   {friend.name.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                   <h4 className="font-bold text-white truncate">{friend.name}</h4>
                                   <p className="text-xs text-gray-500 truncate">@{friend.username}</p>
                                   <div className="flex items-center gap-1 mt-1 text-xs text-green-400">
                                      <Activity size={10}/>
                                      <span>{events_in_common} eventos común</span>
                                   </div>
                                </div>
                                <button 
                                   onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'friend', id: friend.id, name: friend.name }); }}
                                   className="p-2 text-gray-600 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                >
                                   <X size={18}/>
                                </button>
                             </div>
                          ))
                      )}

                      {!loading && friends.length === 0 && (
                        <div className="col-span-full py-10 text-center text-gray-500 animate-fade-in">
                           No tienes amigos agregados aún. ¡Usa el buscador de arriba!
                        </div>
                      )}
                   </div>
                </div>
             </div>
          )}

          {/* TAB 3: REQUESTS */}
          {activeTab === 'requests' && (
             <div className="max-w-2xl mx-auto space-y-4">
                {loading ? (
                    // Skeletons de solicitudes
                    <>
                        <RequestSkeleton />
                        <RequestSkeleton />
                    </>
                ) : (
                    // Lista real
                    requests.length === 0 ? (
                       <div className="text-center py-20 animate-fade-in">
                          <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-600">
                             <MessageSquare size={32}/>
                          </div>
                          <p className="text-gray-500">No tienes solicitudes pendientes.</p>
                       </div>
                    ) : (
                       requests.map(req => (
                          <div key={req.sender_id} className="bg-[#121217] border border-white/10 rounded-2xl p-5 flex items-center justify-between gap-4 animate-fade-in">
                             <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center font-bold text-white">
                                   {req.sender.name.charAt(0)}
                                </div>
                                <div>
                                   <h4 className="font-bold text-white">{req.sender.name}</h4>
                                   <p className="text-sm text-gray-500">@{req.sender.username} quiere ser tu amigo</p>
                                </div>
                             </div>
                             <div className="flex gap-2">
                                <button onClick={() => handleRequest(req.sender_id, 'accept')} className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"><Check/></button>
                                <button onClick={() => handleRequest(req.sender_id, 'reject')} className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"><X/></button>
                             </div>
                          </div>
                       ))
                    )
                )}
             </div>
          )}
        </div>
      </div>

      {/* CONFIRM DELETE MODAL (Sin cambios) */}
      {deleteTarget && (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-[#18181b] border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
               <h3 className="text-xl font-bold text-white mb-2">¿Estás seguro?</h3>
               <p className="text-gray-400 mb-6">
                  Vas a eliminar a <span className="text-white font-semibold">"{deleteTarget.name}"</span>. Esta acción es irreversible.
               </p>
               <div className="flex gap-3">
                  <button onClick={() => setDeleteTarget(null)} className="flex-1 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl font-medium transition-colors">Cancelar</button>
                  <button onClick={handleGlobalDelete} disabled={deleting} className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-bold transition-colors">
                     {deleting ? 'Eliminando...' : 'Eliminar'}
                  </button>
               </div>
            </div>
         </div>
      )}

      <Footer />
    </div>
  );
}